// AI Match engine for the Recruiter dashboard.
//
// Two analyses share one idea: the recruiter states what is wanted, every
// candidate/vacancy is checked criterion by criterion, and each criterion gets
// a verdict — match, partial, miss or unknown (data missing). A criterion the
// recruiter marks "Must" turns a miss into an exclusion; everything else only
// affects the score. The score is the share of points earned out of the
// criteria actually in use, so leaving a filter blank never lowers anyone.
import { haversineKm, resolveMemberCoords, resolveVacancyCoords, isPreferredLocationMatch } from './keralaGeo';

// ─── Text helpers ─────────────────────────────────────────────────────────────
export const tokenize = (v) => String(v || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// fuzzyScore: exact → includes → exact-token → 4-char stem overlap
export const fuzzyScore = (a, b) => {
  if (!a || !b) return 0;
  const la = String(a).toLowerCase().trim(), lb = String(b).toLowerCase().trim();
  if (!la || !lb) return 0;
  if (la === lb) return 1.0;
  if (la.includes(lb) || lb.includes(la)) return 0.85;
  const ta = tokenize(la), tb = tokenize(lb);
  const sbEx = new Set(tb);
  const exact = ta.filter(t => sbEx.has(t)).length;
  if (exact > 0) return exact / Math.max(ta.length, tb.length);
  const sbPfx = new Set(tb.filter(t => t.length >= 3).map(t => t.slice(0, 4)));
  const pfx = ta.filter(t => t.length >= 3 && sbPfx.has(t.slice(0, 4))).length;
  if (pfx > 0) return Math.min(pfx / Math.max(ta.length, tb.length) * 0.65, 0.5);
  return 0;
};

export const parseSalary = (v) => { const nums = String(v || '').replace(/,/g, '').match(/\d+/g); if (!nums) return null; const parsed = nums.map(Number).filter(Number.isFinite); return parsed.length ? Math.max(...parsed) : null; };
export const latestSalary = (reg) => { if (!Array.isArray(reg.experience_details) || !reg.experience_details.length) return null; return parseSalary(reg.experience_details[reg.experience_details.length - 1]?.salary); };

// "15000 - 20000" → {min:15000,max:20000}; "18k" → {min:18000,max:18000}
export const parseNumberRange = (v) => {
  const text = String(v || '').toLowerCase().replace(/,/g, '');
  const nums = [...text.matchAll(/(\d+(?:\.\d+)?)\s*(k|lakh|lac|l)?\b/g)].map(([, n, unit]) => {
    const x = Number(n);
    if (unit === 'k') return x * 1000;
    if (unit === 'lakh' || unit === 'lac' || unit === 'l') return x * 100000;
    return x;
  }).filter(Number.isFinite);
  if (!nums.length) return null;
  return { min: Math.min(...nums), max: Math.max(...nums) };
};

// Age limits: "25-35", "below 35", "above 21", "35" (read as an upper limit). Values outside 14–80 are ignored.
export const parseAgeLimit = (v) => {
  const text = String(v || '').toLowerCase();
  const nums = (text.match(/\d+/g) || []).map(Number).filter((n) => n >= 14 && n <= 80);
  if (!nums.length) return null;
  if (nums.length >= 2) return { min: Math.min(...nums), max: Math.max(...nums) };
  if (/(above|over|min|\+|from|at least)/.test(text)) return { min: nums[0], max: null };
  return { min: null, max: nums[0] };
};

export const candidateAge = (reg) => { const n = parseInt(String(reg?.age || '').replace(/\D/g, ''), 10); return n >= 14 && n <= 80 ? n : null; };

export const normalizeGender = (v) => {
  const s = String(v || '').toLowerCase().trim();
  if (!s) return '';
  if (/(any|both|all|male\s*\/\s*female|female\s*\/\s*male)/.test(s)) return 'Any';
  if (/^(f|female|woman|women|lady|ladies|girl)/.test(s)) return 'Female';
  if (/^(m|male|man|men|gents|boy)/.test(s)) return 'Male';
  return '';
};

export const normalizeMarital = (v) => {
  const s = String(v || '').toLowerCase().trim();
  if (!s) return '';
  if (/(unmarried|single|bachelor|spinster)/.test(s)) return 'Single';
  if (/married/.test(s)) return 'Married';
  if (/(divorc|widow|separat)/.test(s)) return 'Other';
  return '';
};

// ─── Qualification matching ───────────────────────────────────────────────────
export const QUAL_GROUPS = {
  sslc:         ['sslc', '10th', '10', 'secondary', 'matriculation', 'matric', 'tenth'],
  plustwo:      ['plus two', '+2', '12th', '12', 'higher secondary', 'hse', 'intermediate', 'puc', 'plus 2', 'plusone', 'plus one', 'eleventh', 'twelfth'],
  iti:          ['iti', 'i.t.i', 'i t i', 'industrial training'],
  diploma:      ['diploma'],
  bcom:         ['b.com', 'bcom', 'b com', 'bachelor of commerce', 'bachelor commerce', 'commerce graduate', 'bcom graduate'],
  bsc:          ['b.sc', 'bsc', 'b sc', 'bachelor of science', 'science graduate'],
  ba:           ['b.a', 'ba', 'b a', 'bachelor of arts', 'arts graduate'],
  btech:        ['b.tech', 'btech', 'b tech', 'bachelor of technology', 'b.e', 'be', 'engineering graduate', 'b.e.'],
  bba:          ['bba', 'b.b.a', 'bachelor of business administration', 'business administration'],
  bca:          ['bca', 'b.c.a', 'bachelor of computer application', 'computer application'],
  bpharm:       ['b.pharm', 'bpharm', 'b pharm', 'bachelor of pharmacy'],
  llb:          ['llb', 'l.l.b', 'bachelor of law', 'law graduate', 'b.l'],
  mba:          ['mba', 'm.b.a', 'master of business administration', 'pgdm'],
  mcom:         ['m.com', 'mcom', 'm com', 'master of commerce'],
  msc:          ['m.sc', 'msc', 'm sc', 'master of science'],
  ma:           ['m.a', 'ma', 'm a', 'master of arts'],
  mtech:        ['m.tech', 'mtech', 'm tech', 'master of technology'],
  mca:          ['mca', 'm.c.a', 'master of computer application'],
  mpharm:       ['m.pharm', 'mpharm', 'm pharm', 'master of pharmacy'],
  llm:          ['llm', 'l.l.m', 'master of law'],
  phd:          ['phd', 'ph.d', 'ph d', 'doctorate', 'doctor of philosophy'],
  graduate:     ['any graduate', 'graduate', 'degree holder', 'any degree', 'any graduation', 'graduation'],
  postgraduate: ['post graduate', 'postgraduate', 'pg', 'any pg', 'master', 'masters', 'post-graduate', 'any masters', 'any post graduate'],
};
export const BACHELOR_GROUPS = new Set(['bcom','bsc','ba','btech','bba','bca','bpharm','llb']);
export const MASTER_GROUPS   = new Set(['mba','mcom','msc','ma','mtech','mca','mpharm','llm']);

export const normalizeQual = (q) => {
  const s = String(q || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  for (const [group, aliases] of Object.entries(QUAL_GROUPS)) {
    if (aliases.some((a) => s === a || s.includes(a) || a.includes(s))) return group;
  }
  return s;
};

export const qualMatch = (memberQual, vacancyQual) => {
  if (!memberQual) return 0;
  if (!vacancyQual) return 0.3; // vacancy has no requirement — partial credit
  const mNorm = normalizeQual(memberQual);
  const vNorm = normalizeQual(vacancyQual);
  if (!mNorm || !vNorm) return fuzzyScore(memberQual, vacancyQual);
  if (mNorm === vNorm) return 1.0;
  // "Any Graduate" / "Graduate" in vacancy → any bachelor's matches
  if (vNorm === 'graduate' && BACHELOR_GROUPS.has(mNorm)) return 0.95;
  // "Any PG" / "Masters" in vacancy → any master's matches
  if (vNorm === 'postgraduate' && MASTER_GROUPS.has(mNorm)) return 0.95;
  // Overqualified: member has master's, vacancy wants graduate/bachelor's
  if (MASTER_GROUPS.has(mNorm) && (vNorm === 'graduate' || BACHELOR_GROUPS.has(vNorm))) return 0.75;
  // Overqualified: member has bachelor's, vacancy wants Plus Two / ITI / Diploma
  if (BACHELOR_GROUPS.has(mNorm) && ['plustwo','iti','diploma'].includes(vNorm)) return 0.65;
  // Member is graduate, vacancy says postgraduate → under-qualified
  if (BACHELOR_GROUPS.has(mNorm) && (vNorm === 'postgraduate' || MASTER_GROUPS.has(vNorm))) return 0.3;
  return fuzzyScore(memberQual, vacancyQual);
};

// Education ladder used for "minimum qualification" filters.
export const QUAL_LEVELS = [
  { key: 'any', label: 'Any', level: 0 },
  { key: 'sslc', label: 'SSLC / 10th', level: 1 },
  { key: 'plustwo', label: 'Plus Two', level: 2 },
  { key: 'diploma', label: 'ITI / Diploma', level: 3 },
  { key: 'graduate', label: 'Graduate', level: 4 },
  { key: 'postgraduate', label: 'Post Graduate', level: 5 },
];

export const qualLevel = (text) => {
  const raw = String(text || '').toLowerCase();
  if (!raw.trim()) return null;
  if (/pre[\s-]?degree/.test(raw)) return 2;
  const n = normalizeQual(raw);
  if (n === 'phd') return 6;
  if (n === 'postgraduate' || MASTER_GROUPS.has(n)) return 5;
  if (n === 'graduate' || BACHELOR_GROUPS.has(n)) return 4;
  if (n === 'iti' || n === 'diploma') return 3;
  if (n === 'plustwo') return 2;
  if (n === 'sslc') return 1;
  return null;
};
export const qualLevelLabel = (level) => (level === 6 ? 'PhD' : QUAL_LEVELS.find((q) => q.level === level)?.label || '');

// ─── Experience ───────────────────────────────────────────────────────────────
export const experienceRecords = (reg) => (Array.isArray(reg?.experience_details) ? reg.experience_details.filter((e) => e?.title_post || e?.company_name || e?.exp_department || e?.salary) : []);
export const isFresher = (reg) => experienceRecords(reg).length === 0;

// Vacancy experience text → { fresherOk, minYears }
export const parseVacancyExperience = (v) => {
  const s = String(v || '').toLowerCase();
  if (!s.trim()) return null;
  const fresherOk = /(fresher|no experience|not required|0\s*(?:-|to)?\s*\d*\s*y)/.test(s);
  const years = (s.match(/\d+/g) || []).map(Number).filter((n) => n < 40);
  const minYears = fresherOk ? 0 : (years.length ? Math.min(...years) : null);
  if (!fresherOk && minYears === null) return null;
  return { fresherOk, minYears };
};

// ─── Verdict helpers ──────────────────────────────────────────────────────────
const crit = (key, label, icon, weight, status, ratio, detail) => ({
  key, label, icon, weight, status, detail,
  earned: Math.round(weight * Math.max(0, Math.min(1, ratio)) * 10) / 10,
});
const fromFuzzy = (fs) => (fs >= 0.85 ? ['match', 1] : fs >= 0.5 ? ['partial', fs] : fs > 0 ? ['partial', fs * 0.7] : ['miss', 0]);

export const bandFor = (score) => {
  if (score === null || score === undefined) return { label: 'Not scored', tone: 'slate' };
  if (score >= 80) return { label: 'Excellent', tone: 'emerald' };
  if (score >= 65) return { label: 'Strong', tone: 'sky' };
  if (score >= 45) return { label: 'Fair', tone: 'amber' };
  return { label: 'Low', tone: 'rose' };
};

const distanceKm = (a, b) => (a && b ? Math.round(haversineKm(a.lat, a.lon, b.lat, b.lon) * 10) / 10 : null);

const locationCriterion = ({ center, point, radiusKm, weight, textFallback }) => {
  const radius = Number(radiusKm) > 0 ? Number(radiusKm) : null;
  if (center && point) {
    const km = distanceKm(center, point);
    const approx = center.precision === 'district' || point.precision === 'district';
    const kmText = `${approx ? '~' : ''}${km} km`;
    if (radius) {
      if (km <= radius) return { km, approx, c: crit('location', 'Location', '📍', weight, 'match', 1 - 0.4 * (km / radius), `${kmText} · inside ${radius} km`) };
      return { km, approx, c: crit('location', 'Location', '📍', weight, 'miss', 0, `${kmText} · outside ${radius} km`) };
    }
    if (km <= 10) return { km, approx, c: crit('location', 'Location', '📍', weight, 'match', 1, `${kmText} · very close`) };
    if (km <= 25) return { km, approx, c: crit('location', 'Location', '📍', weight, 'match', 0.85, `${kmText} · easy commute`) };
    if (km <= 50) return { km, approx, c: crit('location', 'Location', '📍', weight, 'partial', 0.55, `${kmText} · long commute`) };
    return { km, approx, c: crit('location', 'Location', '📍', weight, 'miss', 0.15, `${kmText} · may need relocation`) };
  }
  if (!radius && textFallback) {
    const fs = textFallback();
    if (fs > 0) { const [st, r] = fromFuzzy(fs); return { km: null, c: crit('location', 'Location', '📍', weight, st, r, 'Place name matches') }; }
  }
  return { km: null, c: crit('location', 'Location', '📍', weight, 'unknown', 0, 'Location not known') };
};

const finish = (criteria, must, { strictUnknown = false, radiusActive = false, includeUnknownLocation = false } = {}) => {
  let excludedBy = null;
  for (const c of criteria) {
    const isRadius = c.key === 'location' && radiusActive;
    if (isRadius && c.status === 'miss') { excludedBy = c; break; }
    if (isRadius && c.status === 'unknown' && !includeUnknownLocation) { excludedBy = c; break; }
    if (must[c.key] && c.status === 'miss') { excludedBy = c; break; }
    if (must[c.key] && c.status === 'unknown' && strictUnknown) { excludedBy = c; break; }
  }
  const total = criteria.reduce((s, c) => s + c.weight, 0);
  const earned = criteria.reduce((s, c) => s + c.earned, 0);
  const score = total > 0 ? Math.round((earned / total) * 100) : null;
  return { score, band: bandFor(score), criteria, excludedBy };
};

// ─── Analysis 1: vacancies for a candidate ───────────────────────────────────
// filters: { designation, department, location, radiusKm, expectedSalary, companyTypes[],
//            checkEligibility, must{}, strictUnknown, includeUnknownLocation }
// profile: the candidate (optional) — used for eligibility (gender/age/qualification/experience).
export const scoreVacancyForCandidate = (vac, filters, { center, profile } = {}) => {
  const f = filters;
  const criteria = [];

  if (f.designation?.trim()) {
    const fs = Math.max(fuzzyScore(f.designation, vac.title), fuzzyScore(f.designation, `${vac.title} ${vac.department || ''}`) * 0.9);
    const [st, r] = fromFuzzy(fs);
    criteria.push(crit('designation', 'Designation', '💼', 28, st, r, st === 'miss' ? `Role is ${vac.title}` : vac.title));
  }
  if (f.department?.trim()) {
    const fs = Math.max(fuzzyScore(f.department, vac.department), fuzzyScore(f.department, vac.section));
    if (!vac.department && !vac.section) criteria.push(crit('department', 'Department', '🏷️', 12, 'unknown', 0, 'Not stated by company'));
    else { const [st, r] = fromFuzzy(fs); criteria.push(crit('department', 'Department', '🏷️', 12, st, r, vac.department || vac.section)); }
  }

  let km = null, approx = false;
  if (center || f.location?.trim()) {
    const point = resolveVacancyCoords(vac);
    const loc = locationCriterion({
      center, point, radiusKm: f.radiusKm, weight: 20,
      textFallback: () => fuzzyScore(f.location, `${vac.location || ''} ${vac.district || ''}`),
    });
    km = loc.km; approx = loc.approx;
    criteria.push(loc.c);
  }

  const expected = Number(String(f.expectedSalary || '').replace(/\D/g, '')) || null;
  if (expected) {
    const range = parseNumberRange(vac.salary);
    if (!range) criteria.push(crit('salary', 'Salary', '💰', 15, 'unknown', 0, 'Salary not stated'));
    else if (range.max >= expected) criteria.push(crit('salary', 'Salary', '💰', 15, 'match', 1, `Offers ${vac.salary}`));
    else if (range.max >= expected * 0.85) criteria.push(crit('salary', 'Salary', '💰', 15, 'partial', 0.55, `Offers ${vac.salary} · slightly below`));
    else criteria.push(crit('salary', 'Salary', '💰', 15, 'miss', 0, `Offers ${vac.salary} · below ₹${expected.toLocaleString('en-IN')}`));
  }

  const types = (f.companyTypes || []).filter(Boolean);
  if (types.length) {
    const cat = vac.company_category || vac.category || '';
    if (!cat) criteria.push(crit('companyType', 'Company type', '🏢', 10, 'unknown', 0, 'Company type not recorded'));
    else {
      const fs = Math.max(...types.map((t) => fuzzyScore(t, cat)));
      const [st, r] = fromFuzzy(fs);
      criteria.push(crit('companyType', 'Company type', '🏢', 10, st, r, cat));
    }
  }

  if (f.checkEligibility && profile) {
    const issues = [], unknown = [];
    const vg = normalizeGender(vac.gender), mg = normalizeGender(profile.gender);
    if (vg && vg !== 'Any') { if (!mg) unknown.push('gender'); else if (vg !== mg) issues.push(`${vg} only`); }
    const ageLimit = parseAgeLimit(vac.age_limit), age = candidateAge(profile);
    if (ageLimit) {
      if (age === null) unknown.push('age');
      else if ((ageLimit.min && age < ageLimit.min) || (ageLimit.max && age > ageLimit.max)) issues.push(`Age limit ${vac.age_limit}`);
    }
    const needLevel = qualLevel(vac.qualification), haveLevel = qualLevel(profile.highest_qualification);
    if (needLevel) {
      if (!haveLevel) unknown.push('qualification');
      else if (haveLevel < needLevel) issues.push(`Needs ${vac.qualification}`);
    }
    const ve = parseVacancyExperience(vac.experience);
    if (ve && !ve.fresherOk && ve.minYears > 0 && isFresher(profile)) issues.push(`Needs ${vac.experience} experience`);
    if (issues.length) criteria.push(crit('eligibility', 'Eligibility', '✅', 15, 'miss', 0, issues.join(' · ')));
    else if (unknown.length) criteria.push(crit('eligibility', 'Eligibility', '✅', 15, 'partial', 0.6, `Check ${unknown.join(', ')}`));
    else criteria.push(crit('eligibility', 'Eligibility', '✅', 15, 'match', 1, 'Meets company requirements'));
  }

  const result = finish(criteria, f.must || {}, {
    strictUnknown: f.strictUnknown, includeUnknownLocation: f.includeUnknownLocation,
    radiusActive: Number(f.radiusKm) > 0 && Boolean(center),
  });
  return { ...result, km, approx, preferred: profile ? isPreferredLocationMatch(profile, vac) : false };
};

// ─── Analysis 2: candidates for a company ────────────────────────────────────
// filters: { designation, department, gender, ageMin, ageMax, marital, minQualLevel, qualText,
//            location, radiusKm, salaryBudget, experience ('Any'|'Fresher'|'Experienced'),
//            must{}, strictUnknown, includeUnknownLocation }
export const scoreCandidateForCompany = (reg, filters, { center } = {}) => {
  const f = filters;
  const criteria = [];
  const pastTitles = experienceRecords(reg).map((e) => e.title_post).filter(Boolean);

  if (f.designation?.trim()) {
    const wanted = fuzzyScore(f.designation, reg.expecting_job);
    const done = pastTitles.length ? Math.max(...pastTitles.map((t) => fuzzyScore(f.designation, t))) * 0.95 : 0;
    const fs = Math.max(wanted, done);
    if (!reg.expecting_job && !pastTitles.length) criteria.push(crit('designation', 'Designation', '💼', 25, 'unknown', 0, 'No job preference recorded'));
    else {
      const [st, r] = fromFuzzy(fs);
      const detail = done >= wanted && done > 0 ? `Worked as ${pastTitles.find((t) => fuzzyScore(f.designation, t) * 0.95 === done) || pastTitles[0]}` : `Wants ${reg.expecting_job || '—'}`;
      criteria.push(crit('designation', 'Designation', '💼', 25, st, r, detail));
    }
  }
  if (f.department?.trim()) {
    const fs = Math.max(fuzzyScore(f.department, reg.department), fuzzyScore(f.department, reg.section), fuzzyScore(f.department, reg.qualification_department) * 0.8,
      ...experienceRecords(reg).map((e) => fuzzyScore(f.department, e.exp_department) * 0.9));
    if (!reg.department && !reg.section) criteria.push(crit('department', 'Department', '🏷️', 12, 'unknown', 0, 'Department not recorded'));
    else { const [st, r] = fromFuzzy(fs); criteria.push(crit('department', 'Department', '🏷️', 12, st, r, reg.department || reg.section)); }
  }

  let km = null, approx = false;
  if (center || f.location?.trim()) {
    const point = resolveMemberCoords(reg);
    const loc = locationCriterion({
      center, point, radiusKm: f.radiusKm, weight: 20,
      textFallback: () => Math.max(fuzzyScore(f.location, reg.place), fuzzyScore(f.location, reg.district), fuzzyScore(f.location, reg.preferred_location)),
    });
    km = loc.km; approx = loc.approx;
    criteria.push(loc.c);
  }

  if (f.gender && f.gender !== 'Any') {
    const g = normalizeGender(reg.gender);
    criteria.push(!g ? crit('gender', 'Gender', '⚧', 8, 'unknown', 0, 'Gender not recorded')
      : crit('gender', 'Gender', '⚧', 8, g === f.gender ? 'match' : 'miss', g === f.gender ? 1 : 0, g));
  }

  const ageMin = Number(f.ageMin) || null, ageMax = Number(f.ageMax) || null;
  if (ageMin || ageMax) {
    const age = candidateAge(reg);
    if (age === null) criteria.push(crit('age', 'Age', '🎂', 8, 'unknown', 0, 'Age not recorded'));
    else {
      const below = ageMin && age < ageMin ? ageMin - age : 0, above = ageMax && age > ageMax ? age - ageMax : 0;
      const off = below || above;
      if (!off) criteria.push(crit('age', 'Age', '🎂', 8, 'match', 1, `${age} yrs`));
      else if (off <= 2) criteria.push(crit('age', 'Age', '🎂', 8, 'partial', 0.4, `${age} yrs · just ${below ? 'below' : 'above'}`));
      else criteria.push(crit('age', 'Age', '🎂', 8, 'miss', 0, `${age} yrs`));
    }
  }

  if (f.marital && f.marital !== 'Any') {
    const m = normalizeMarital(reg.marital_status);
    criteria.push(!m ? crit('marital', 'Marital status', '💍', 4, 'unknown', 0, 'Not recorded')
      : crit('marital', 'Marital status', '💍', 4, m === f.marital ? 'match' : 'miss', m === f.marital ? 1 : 0, reg.marital_status));
  }

  const minLevel = Number(f.minQualLevel) || 0;
  if (minLevel || f.qualText?.trim()) {
    const qualText = `${reg.highest_qualification || ''} ${reg.qualification_department || ''} ${reg.additional_qualification || ''}`;
    const have = qualLevel(reg.highest_qualification);
    let status = 'match', ratio = 1;
    const notes = [reg.highest_qualification || 'Not recorded'];
    if (minLevel) {
      if (!have) { status = reg.highest_qualification ? 'partial' : 'unknown'; ratio = reg.highest_qualification ? 0.4 : 0; }
      else if (have < minLevel) { status = have === minLevel - 1 ? 'partial' : 'miss'; ratio = have === minLevel - 1 ? 0.35 : 0; }
      else if (have - minLevel >= 2) notes.push('over-qualified');
    }
    if (f.qualText?.trim() && status !== 'unknown') {
      const fs = Math.max(qualMatch(reg.highest_qualification, f.qualText), fuzzyScore(f.qualText, qualText));
      if (fs < 0.5) { status = status === 'match' ? 'partial' : status; ratio = Math.min(ratio, 0.4 + fs); notes.push(`not ${f.qualText}`); }
    }
    criteria.push(crit('qualification', 'Qualification', '🎓', 15, status, ratio, notes.join(' · ')));
  }

  if (f.experience && f.experience !== 'Any') {
    const n = experienceRecords(reg).length;
    if (f.experience === 'Fresher') criteria.push(crit('experience', 'Experience', '🧭', 10, n === 0 ? 'match' : 'partial', n === 0 ? 1 : 0.3, n === 0 ? 'Fresher' : `${n} job${n > 1 ? 's' : ''} before`));
    else {
      const relevant = f.designation && pastTitles.some((t) => fuzzyScore(f.designation, t) >= 0.5);
      criteria.push(n > 0
        ? crit('experience', 'Experience', '🧭', 10, 'match', relevant ? 1 : 0.8, relevant ? 'Relevant experience' : `${n} job${n > 1 ? 's' : ''} before`)
        : crit('experience', 'Experience', '🧭', 10, 'miss', 0, 'Fresher'));
    }
  }

  const budget = Number(String(f.salaryBudget || '').replace(/\D/g, '')) || null;
  if (budget) {
    const cs = latestSalary(reg);
    if (!cs) criteria.push(crit('salary', 'Salary', '💰', 8, 'unknown', 0, 'Salary not recorded'));
    else if (cs <= budget) criteria.push(crit('salary', 'Salary', '💰', 8, 'match', 1, `Last drew ₹${cs.toLocaleString('en-IN')}`));
    else if (cs <= budget * 1.15) criteria.push(crit('salary', 'Salary', '💰', 8, 'partial', 0.5, `Last drew ₹${cs.toLocaleString('en-IN')}`));
    else criteria.push(crit('salary', 'Salary', '💰', 8, 'miss', 0, `Last drew ₹${cs.toLocaleString('en-IN')}`));
  }

  const result = finish(criteria, f.must || {}, {
    strictUnknown: f.strictUnknown, includeUnknownLocation: f.includeUnknownLocation,
    radiusActive: Number(f.radiusKm) > 0 && Boolean(center),
  });
  return { ...result, km, approx };
};

// ─── Auto-fill helpers ───────────────────────────────────────────────────────
export const filtersFromCandidate = (reg) => {
  const pref = Array.isArray(reg.preferred_district) ? reg.preferred_district[0] : String(reg.preferred_district || '').split(',')[0];
  const salary = latestSalary(reg);
  return {
    designation: reg.expecting_job || '',
    department: reg.department || '',
    location: reg.google_location || reg.place || reg.preferred_location || pref || reg.district || '',
    expectedSalary: salary ? String(salary) : '',
  };
};

export const filtersFromVacancy = (vac) => {
  const age = parseAgeLimit(vac.age_limit);
  const gender = normalizeGender(vac.gender);
  const ve = parseVacancyExperience(vac.experience);
  const range = parseNumberRange(vac.salary);
  const level = qualLevel(vac.qualification);
  return {
    designation: vac.title || '',
    department: vac.department || '',
    gender: gender || 'Any',
    ageMin: age?.min ? String(age.min) : '',
    ageMax: age?.max ? String(age.max) : '',
    minQualLevel: level ? String(Math.min(level, 5)) : '0',
    qualText: level && level >= 4 && normalizeQual(vac.qualification) !== 'graduate' && normalizeQual(vac.qualification) !== 'postgraduate' ? vac.qualification : '',
    experience: ve ? (ve.fresherOk ? 'Any' : 'Experienced') : 'Any',
    salaryBudget: range ? String(range.max) : '',
    // One place only: "Kakkanad, Ernakulam" would resolve to the district centre instead of the town.
    location: vac.company_map || vac.location || vac.district || '',
  };
};
